import { useEffect, useMemo, useState } from 'react';
import type { TestGuidance, TestType } from '@sr-test-wizard/shared';
import {
  testGuidanceApi,
  type CreateGuidanceInput,
  type UpdateGuidanceInput,
} from '../../lib/testGuidanceApi';
import { formatAppDate, formatAppDateTime } from '../../lib/localTime';
import { ontologyApi } from '../../lib/ontologyApi';

const TEST_KEY_RE = /^[A-Z]+-\d+$/;

const TEST_TYPES: TestType[] = ['reasoning', 'recognition', 'observation', 'measurement'];
const TEST_TYPE_HINT: Record<TestType, string> = {
  reasoning: 'Full source-grounded aggregator (default).',
  recognition: 'Ontology-mapping heavy; aggregator with concept cards.',
  observation: 'Non-LLM vision persistence-check across frames (e.g. clock visible at all times).',
  measurement: 'Non-LLM timestamp arithmetic (e.g. spin >= 2.5s).',
};

const FIELD_CAPS = {
  description: 4000,
  passedCriteria: 2000,
  failedCriteria: 2000,
  naCriteria: 2000,
  naComment: 2000,
} as const;

interface DraftValues {
  testKey: string;
  description: string;
  passedCriteria: string;
  failedCriteria: string;
  naCriteria: string;
  naComment: string;
  conceptIdsText: string;
  testType: TestType;
}

const EMPTY_DRAFT: DraftValues = {
  testKey: '',
  description: '',
  passedCriteria: '',
  failedCriteria: '',
  naCriteria: '',
  naComment: '',
  conceptIdsText: '',
  testType: 'reasoning',
};

function draftFromEntry(entry: TestGuidance): DraftValues {
  return {
    testKey: entry.testKey,
    description: entry.description,
    passedCriteria: entry.passedCriteria,
    failedCriteria: entry.failedCriteria,
    naCriteria: entry.naCriteria,
    naComment: entry.naComment,
    conceptIdsText: (entry.conceptIds ?? []).join(', '),
    testType: entry.testType ?? 'reasoning',
  };
}

function parseConceptIds(text: string): string[] {
  return text
    .split(/[,\n]/)
    .map((s) => s.trim())
    .filter(Boolean);
}

function hasBody(d: DraftValues): boolean {
  return Boolean(
    d.description.trim() ||
      d.passedCriteria.trim() ||
      d.failedCriteria.trim() ||
      d.naCriteria.trim(),
  );
}

interface FieldEditorProps {
  label: string;
  value: string;
  cap: number;
  rows?: number;
  placeholder?: string;
  disabled?: boolean;
  onChange: (next: string) => void;
}

function FieldEditor({ label, value, cap, rows = 3, placeholder, disabled, onChange }: FieldEditorProps) {
  return (
    <div className="flex-col" style={{ gap: 4 }}>
      <div className="row" style={{ gap: 8, alignItems: 'center' }}>
        <label className="muted small" style={{ minWidth: 0 }}>
          {label}
        </label>
        <span className="spacer" />
        <span className="muted small mono">
          {value.length}/{cap}
        </span>
      </div>
      <textarea
        value={value}
        maxLength={cap}
        onChange={(e) => onChange(e.target.value)}
        rows={rows}
        placeholder={placeholder}
        style={{ resize: 'vertical', minHeight: rows * 24 }}
        disabled={disabled}
      />
    </div>
  );
}

interface GuidanceFormProps {
  draft: DraftValues;
  busy: boolean;
  error: string | null;
  mode: 'create' | 'edit';
  onChange: (next: DraftValues) => void;
  onCancel: () => void;
  onSubmit: () => void;
}

function GuidanceForm({ draft, busy, error, mode, onChange, onCancel, onSubmit }: GuidanceFormProps) {
  const testKeyValid = TEST_KEY_RE.test(draft.testKey.trim());
  const canSave =
    testKeyValid &&
    (hasBody(draft) || draft.conceptIdsText.trim().length > 0 || draft.testType !== 'reasoning');
  const [suggesting, setSuggesting] = useState(false);
  const [suggestions, setSuggestions] = useState<
    Array<{ slug: string; canonicalName: string; method: string; score: number }>
  >([]);
  const [suggestNote, setSuggestNote] = useState<string | null>(null);

  const runSuggest = async () => {
    const text = [draft.description, draft.passedCriteria, draft.failedCriteria].join('\n').trim();
    if (!text) {
      setSuggestNote('Add some Notes / criteria first so there is text to recognise.');
      return;
    }
    setSuggesting(true);
    setSuggestNote(null);
    try {
      const res = await ontologyApi.suggest(text);
      setSuggestions(res);
      if (res.length === 0) setSuggestNote('No concept suggestions (embeddings API may be unavailable).');
    } catch (err) {
      setSuggestNote((err as Error).message);
    } finally {
      setSuggesting(false);
    }
  };

  const addSuggestion = (slug: string) => {
    const current = parseConceptIds(draft.conceptIdsText);
    if (current.includes(slug)) return;
    onChange({ ...draft, conceptIdsText: [...current, slug].join(', ') });
  };

  return (
    <div className="card" style={{ padding: 12 }}>
      <div className="row" style={{ gap: 8 }}>
        <h3 style={{ margin: 0 }}>
          {mode === 'create' ? 'Add guidance' : `Edit ${draft.testKey || 'guidance'}`}
        </h3>
        <span className="spacer" />
        <button type="button" className="btn small btn-ghost" onClick={onCancel} disabled={busy}>
          Cancel
        </button>
      </div>
      <div className="flex-col" style={{ gap: 10, marginTop: 10 }}>
        <div className="row" style={{ gap: 8, alignItems: 'center' }}>
          <label className="muted small" style={{ minWidth: 110 }}>
            Test key
          </label>
          <input
            value={draft.testKey}
            maxLength={60}
            onChange={(e) => onChange({ ...draft, testKey: e.target.value.toUpperCase() })}
            placeholder="TLIB-20"
            style={{ flex: 1, fontFamily: 'monospace' }}
            disabled={busy}
          />
          {!testKeyValid && draft.testKey.trim() ? (
            <span className="badge small bad" title="Expected format: PROJECT-NUMBER, e.g. TLIB-20">
              Invalid format
            </span>
          ) : (
            <span className="muted small">e.g. TLIB-20, XPT-12</span>
          )}
        </div>
        <div className="row" style={{ gap: 8, alignItems: 'center' }}>
          <label className="muted small" style={{ minWidth: 110 }}>
            Test type
          </label>
          <select
            value={draft.testType}
            disabled={busy}
            onChange={(e) => onChange({ ...draft, testType: e.target.value as TestType })}
          >
            {TEST_TYPES.map((t) => (
              <option key={t} value={t}>{t}</option>
            ))}
          </select>
          <span className="muted small">{TEST_TYPE_HINT[draft.testType]}</span>
        </div>
        <div className="flex-col" style={{ gap: 4 }}>
          <div className="row" style={{ gap: 8, alignItems: 'center' }}>
            <label className="muted small" style={{ minWidth: 110 }}>
              Concept slugs
            </label>
            <input
              value={draft.conceptIdsText}
              disabled={busy}
              placeholder="buy_feature, gamble (comma-separated; only these cards are injected)"
              style={{ flex: 1 }}
              onChange={(e) => onChange({ ...draft, conceptIdsText: e.target.value })}
            />
            <button type="button" className="btn small" onClick={() => void runSuggest()} disabled={busy || suggesting}>
              {suggesting ? 'Suggesting…' : 'Suggest'}
            </button>
          </div>
          {suggestNote && <span className="muted small">{suggestNote}</span>}
          {suggestions.length > 0 && (
            <div className="row" style={{ gap: 6, flexWrap: 'wrap' }}>
              {suggestions.map((s) => (
                <button
                  key={s.slug}
                  type="button"
                  className="btn small btn-ghost"
                  title={`${s.method} · ${s.score}`}
                  onClick={() => addSuggestion(s.slug)}
                >
                  + {s.canonicalName} ({s.slug})
                </button>
              ))}
            </div>
          )}
        </div>
        <FieldEditor
          label="Notes (extra context beyond the Xray task)"
          value={draft.description}
          cap={FIELD_CAPS.description}
          rows={4}
          placeholder='Clarifications, gotchas, "NB!" notes, regulatory background... anything the AI should know on top of what is already in the Xray task summary / description / steps. Do not duplicate evidence requirements - those are read straight from Xray.'
          disabled={busy}
          onChange={(v) => onChange({ ...draft, description: v })}
        />
        <FieldEditor
          label="PASSED criteria"
          value={draft.passedCriteria}
          cap={FIELD_CAPS.passedCriteria}
          rows={3}
          placeholder="When should the AI mark this PASSED?"
          disabled={busy}
          onChange={(v) => onChange({ ...draft, passedCriteria: v })}
        />
        <FieldEditor
          label="FAILED criteria"
          value={draft.failedCriteria}
          cap={FIELD_CAPS.failedCriteria}
          rows={3}
          placeholder="When should the AI mark this FAILED?"
          disabled={busy}
          onChange={(v) => onChange({ ...draft, failedCriteria: v })}
        />
        <FieldEditor
          label="N/A criteria"
          value={draft.naCriteria}
          cap={FIELD_CAPS.naCriteria}
          rows={2}
          placeholder='When does the test not apply? Write "NA" if there is no N/A case.'
          disabled={busy}
          onChange={(v) => onChange({ ...draft, naCriteria: v })}
        />
        <FieldEditor
          label="N/A comment (auto-appended)"
          value={draft.naComment}
          cap={FIELD_CAPS.naComment}
          rows={3}
          placeholder="If you set this, it is auto-appended to the AI's rationale whenever the verdict comes back as N/A. Leave empty to skip."
          disabled={busy}
          onChange={(v) => onChange({ ...draft, naComment: v })}
        />
        <div className="row" style={{ gap: 8, justifyContent: 'flex-end' }}>
          <span className="spacer" />
          <button
            type="button"
            className="btn small btn-primary"
            onClick={onSubmit}
            disabled={busy || !canSave}
            title={!canSave ? 'Test key must match PROJECT-NUMBER and at least one body field must be filled' : undefined}
          >
            {busy ? 'Saving…' : mode === 'create' ? 'Add guidance' : 'Save changes'}
          </button>
        </div>
        {error && (
          <div className="muted small" style={{ color: 'var(--bad)' }}>
            {error}
          </div>
        )}
      </div>
    </div>
  );
}

interface EntryCardProps {
  entry: TestGuidance;
  onEdit: () => void;
  onDelete: () => void;
}

function EntryCard({ entry, onEdit, onDelete }: EntryCardProps) {
  return (
    <div className="card" style={{ padding: 12 }}>
      <div className="row" style={{ gap: 8, alignItems: 'center' }}>
        <span className="badge small mono">{entry.testKey}</span>
        <strong style={{ fontSize: 14 }}>
          {entry.description.split('\n')[0] || '(no description)'}
        </strong>
        {entry.testType && entry.testType !== 'reasoning' && (
          <span className="badge small" title="Routing: non-default test type">
            {entry.testType}
          </span>
        )}
        {(entry.conceptIds?.length ?? 0) > 0 && (
          <span className="muted small mono" title="Declared concept slugs">
            {entry.conceptIds.join(', ')}
          </span>
        )}
        <span className="spacer" />
        {entry.naComment && (
          <span
            className="badge small purple"
            title="An N/A comment is configured and will be auto-appended to the AI's rationale when the verdict is N/A."
          >
            N/A comment
          </span>
        )}
        <span
          className="muted small"
          title={`Last edited ${formatAppDateTime(entry.updatedAt)}`}
        >
          {formatAppDate(entry.updatedAt)}
        </span>
        <button type="button" className="btn small" onClick={onEdit}>
          Edit
        </button>
        <button
          type="button"
          className="btn small"
          onClick={onDelete}
          style={{ borderColor: 'var(--bad)', color: 'var(--bad)' }}
        >
          Delete
        </button>
      </div>
      <div className="flex-col" style={{ gap: 6, marginTop: 8 }}>
        {entry.description && <FieldReadOnly label="Notes" value={entry.description} />}
        {entry.passedCriteria && <FieldReadOnly label="PASSED" value={entry.passedCriteria} />}
        {entry.failedCriteria && <FieldReadOnly label="FAILED" value={entry.failedCriteria} />}
        {entry.naCriteria && <FieldReadOnly label="N/A" value={entry.naCriteria} />}
        {entry.naComment && <FieldReadOnly label="N/A comment (auto-appended)" value={entry.naComment} />}
      </div>
    </div>
  );
}

function FieldReadOnly({ label, value }: { label: string; value: string }) {
  return (
    <div className="flex-col" style={{ gap: 2 }}>
      <div className="muted small mono">{label}</div>
      <div style={{ whiteSpace: 'pre-wrap', wordBreak: 'break-word' }}>{value}</div>
    </div>
  );
}

export function TestGuidancePage() {
  const [entries, setEntries] = useState<TestGuidance[]>([]);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState<string | null>(null);

  const [filter, setFilter] = useState('');

  const [adding, setAdding] = useState(false);
  const [addDraft, setAddDraft] = useState<DraftValues>(EMPTY_DRAFT);
  const [addBusy, setAddBusy] = useState(false);
  const [addError, setAddError] = useState<string | null>(null);

  const [editingId, setEditingId] = useState<string | null>(null);
  const [editDraft, setEditDraft] = useState<DraftValues>(EMPTY_DRAFT);
  const [editBusy, setEditBusy] = useState(false);
  const [editError, setEditError] = useState<string | null>(null);

  const refresh = async () => {
    setLoading(true);
    setLoadError(null);
    try {
      const data = await testGuidanceApi.list();
      setEntries(data.entries);
    } catch (err) {
      setLoadError((err as Error).message);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    void refresh();
  }, []);

  const filtered = useMemo(() => {
    const q = filter.trim().toLowerCase();
    if (!q) return entries;
    return entries.filter((e) => {
      const haystack = [
        e.testKey,
        e.description,
        e.passedCriteria,
        e.failedCriteria,
        e.naCriteria,
        e.naComment,
      ]
        .join('\n')
        .toLowerCase();
      return haystack.includes(q);
    });
  }, [entries, filter]);

  const startAdd = () => {
    setAdding(true);
    setAddDraft(EMPTY_DRAFT);
    setAddError(null);
    setEditingId(null);
  };

  const cancelAdd = () => {
    setAdding(false);
    setAddDraft(EMPTY_DRAFT);
    setAddError(null);
  };

  const buildPayload = (d: DraftValues): CreateGuidanceInput => ({
    testKey: d.testKey.trim(),
    description: d.description.trim(),
    passedCriteria: d.passedCriteria.trim(),
    failedCriteria: d.failedCriteria.trim(),
    naCriteria: d.naCriteria.trim(),
    naComment: d.naComment.trim(),
    conceptIds: parseConceptIds(d.conceptIdsText),
    testType: d.testType,
  });

  const submitAdd = async () => {
    setAddBusy(true);
    setAddError(null);
    try {
      const created = await testGuidanceApi.create(buildPayload(addDraft));
      setEntries((prev) => [...prev, created]);
      cancelAdd();
    } catch (err) {
      setAddError((err as Error).message);
    } finally {
      setAddBusy(false);
    }
  };

  const startEdit = (entry: TestGuidance) => {
    setEditingId(entry.id);
    setEditDraft(draftFromEntry(entry));
    setEditError(null);
    setAdding(false);
  };

  const cancelEdit = () => {
    setEditingId(null);
    setEditDraft(EMPTY_DRAFT);
    setEditError(null);
  };

  const submitEdit = async (id: string) => {
    setEditBusy(true);
    setEditError(null);
    try {
      const patch: UpdateGuidanceInput = buildPayload(editDraft);
      const updated = await testGuidanceApi.update(id, patch);
      setEntries((prev) => prev.map((e) => (e.id === id ? updated : e)));
      cancelEdit();
    } catch (err) {
      setEditError((err as Error).message);
    } finally {
      setEditBusy(false);
    }
  };

  const removeOne = async (entry: TestGuidance) => {
    if (!window.confirm(`Delete guidance for ${entry.testKey}?`)) return;
    try {
      await testGuidanceApi.remove(entry.id);
      setEntries((prev) => prev.filter((e) => e.id !== entry.id));
      if (editingId === entry.id) cancelEdit();
    } catch (err) {
      setLoadError((err as Error).message);
    }
  };

  return (
    <div className="flex-col" style={{ gap: 14 }}>
      <div className="row" style={{ gap: 8, alignItems: 'center' }}>
        <h1 style={{ margin: 0 }}>Test guidance</h1>
        <span className="spacer" />
        <input
          value={filter}
          onChange={(e) => setFilter(e.target.value)}
          placeholder="Filter by TLIB or text…"
          style={{ width: 260 }}
        />
        {!adding && (
          <button type="button" className="btn small btn-primary" onClick={startAdd}>
            + Add guidance
          </button>
        )}
      </div>

      <p className="muted small" style={{ marginTop: 0 }}>
        Per-TLIB cheatsheet the AI reads when scoring this specific test and when
        playing it live in Look at that, on top of what Xray already supplies
        (summary, description, steps). Use Notes for clarifications and gotchas
        (for example whether a popup is in scope), then spell out the PASSED /
        FAILED / N/A criteria — live play stops as soon as PASSED is visible.
        The optional N/A comment is automatically appended to the AI's rationale
        whenever a Phase 7 verdict comes back as N/A.
      </p>

      {loadError && (
        <div className="card" style={{ borderColor: 'var(--bad)', color: 'var(--bad)' }}>
          {loadError}
        </div>
      )}

      {adding && (
        <GuidanceForm
          draft={addDraft}
          busy={addBusy}
          error={addError}
          mode="create"
          onChange={setAddDraft}
          onCancel={cancelAdd}
          onSubmit={() => void submitAdd()}
        />
      )}

      {loading ? (
        <div className="muted">Loading…</div>
      ) : entries.length === 0 ? (
        <div className="card">
          <p className="muted" style={{ margin: 0 }}>
            No guidance yet. Click "Add guidance" to seed your first TLIB entry.
          </p>
        </div>
      ) : filtered.length === 0 ? (
        <div className="muted">No guidance matches "{filter}".</div>
      ) : (
        <div className="flex-col" style={{ gap: 10 }}>
          {filtered.map((entry) =>
            editingId === entry.id ? (
              <GuidanceForm
                key={entry.id}
                draft={editDraft}
                busy={editBusy}
                error={editError}
                mode="edit"
                onChange={setEditDraft}
                onCancel={cancelEdit}
                onSubmit={() => void submitEdit(entry.id)}
              />
            ) : (
              <EntryCard
                key={entry.id}
                entry={entry}
                onEdit={() => startEdit(entry)}
                onDelete={() => void removeOne(entry)}
              />
            ),
          )}
        </div>
      )}
    </div>
  );
}
